import { useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SeoHelmet from '@/components/base/SeoHelmet';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9slenh4ne17ei0sdja0';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      setFormMessage('消息已发送，我们会尽快回复！');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    setFormStatus('loading');
    setFormMessage('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, String(value));
      });

      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse errors
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的消息！我们会在1-2个工作日内回复您。');
        form.reset();
      } else {
        const serverMsg = (parsed as { meta?: { message?: string } })?.meta?.message
          || (parsed as { message?: string })?.message
          || responseText
          || '发送失败，请稍后重试';
        setFormStatus('error');
        setFormMessage(String(serverMsg));
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '免费91 网页',
    url: 'https://free91.com',
    description: '免费在线访问入口与资源导航平台，为用户提供便捷的免费在线访问体验，汇聚丰富网页资源与导航服务。',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-991-0000',
      contactType: 'customer service',
      email: 'contact@free91.com',
      availableLanguage: 'Chinese',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '免费91 网页是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '免费91 网页是一个免费的在线资源导航平台，致力于为用户提供便捷的免费在线访问体验。我们汇聚丰富的网页资源与导航服务，无需注册即可直接访问，是发现优质免费网页资源的首选平台。',
        },
      },
      {
        '@type': 'Question',
        name: '免费91 网页上的资源真的全部免费吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的。我们平台推荐的所有资源均为免费或提供免费版本，用户无需支付任何费用即可使用。我们会严格筛选，确保推荐的资源是真正免费的。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系免费91 网页的团队？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过邮箱 contact@free91.com 或客服电话 400-991-0000 联系我们。也可以使用网站上的联系表单提交您的反馈或建议，我们会在1-2个工作日内回复。',
        },
      },
    ],
  };

  return (
    <>
      <SeoHelmet
        title="关于我们 - 品牌介绍与联系方式"
        description="免费91 网页是一个免费的在线资源导航平台，致力于为用户提供便捷的免费在线访问体验。了解我们的品牌故事、用户口碑和联系方式。"
        keywords="免费91 网页,关于免费91,免费91介绍,免费91联系方式,91网页品牌,免费资源平台介绍"
        canonicalUrl="https://free91.com/about"
        schema={[orgSchema, faqSchema]}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        {/* Header */}
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mt-3">
              关于我们
            </h1>
            <p className="text-sm text-foreground-600 mt-2 max-w-2xl">
              了解免费91 网页的品牌故事、发展历程和核心使命
            </p>
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-4">
                  品牌介绍
                </h2>
                <div className="space-y-3 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    <strong className="text-foreground-900">免费91 网页</strong>成立于2024年，是一个专注于免费在线资源导航的创新平台。我们的使命是让每一个互联网用户都能轻松发现和使用优质的免费网络资源。
                  </p>
                  <p>
                    在互联网信息爆炸的时代，优质免费资源往往被淹没在海量信息之中。免费91 网页应运而生，通过专业的内容筛选和分类整理，帮助用户快速找到真正有价值的免费网页资源入口。
                  </p>
                  <p>
                    我们的团队由资深互联网从业者组成，对免费资源领域有着深刻的理解和热情。我们每天浏览和测试大量网站，只为筛选出最优质、最可靠的免费资源推荐给用户。
                  </p>
                  <p>
                    无论是免费影视、免费音乐、免费工具，还是免费学习资源和免费游戏，免费91 网页都能为您提供一站式的导航服务。我们的核心价值观是：<strong className="text-foreground-900">永远免费，始终优质</strong>。
                  </p>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20minimalist%20office%20team%20collaboration%20scene%2C%20creative%20workspace%20with%20natural%20light%2C%20warm%20coral%20and%20amber%20decor%2C%20professional%20atmosphere%2C%20clean%20design%2C%20friendly%20environment%2C%20high%20quality%20photography&width=600&height=450&seq=about-brand-001&orientation=landscape"
                  alt="免费91 网页团队"
                  title="免费91 网页 - 团队协作"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { value: '2024', label: '成立年份' },
                { value: '1000+', label: '收录资源' },
                { value: '50万+', label: '月访问量' },
                { value: '99.9%', label: '服务可用率' },
              ].map((stat, i) => (
                <div key={i} className="text-center p-5 bg-background-50 rounded-xl border border-background-200/70">
                  <div className="text-xl md:text-2xl font-bold text-primary-500">{stat.value}</div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-6 text-center">
              用户口碑
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  name: '张先生',
                  role: '自由职业者',
                  comment: '免费91 网页是我每天必访问的网站，资源分类清晰，更新及时。特别是免费工具板块，帮我省了不少找软件的时间。强烈推荐给所有需要免费资源的朋友！',
                  rating: 5,
                },
                {
                  name: '李女士',
                  role: '大学生',
                  comment: '作为一个学生党，免费学习资源板块简直是福音！通过免费91 网页找到了很多优质的学习平台，零成本提升技能，感谢这个平台的用心整理。',
                  rating: 5,
                },
                {
                  name: '王先生',
                  role: 'IT从业者',
                  comment: '专业的资源导航平台，界面简洁无广告，使用体验很好。推荐的资源质量都很高，看得出来团队在内容筛选上下了功夫。希望继续保持！',
                  rating: 5,
                },
              ].map((review, i) => (
                <div key={i} className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, j) => (
                      <i key={j} className="ri-star-fill text-sm text-accent-500"></i>
                    ))}
                  </div>
                  <p className="text-xs text-foreground-600 leading-relaxed mb-4">
                    "{review.comment}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-semibold text-primary-600">{review.name.charAt(0)}</span>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-foreground-900">{review.name}</div>
                      <div className="text-xs text-foreground-400">{review.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-6 text-center">
              联系我们
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900">电子邮箱</h3>
                    <p className="text-xs text-foreground-500 mt-0.5">contact@free91.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-accent-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900">客服热线</h3>
                    <p className="text-xs text-foreground-500 mt-0.5">400-991-0000</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-secondary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900">工作时间</h3>
                    <p className="text-xs text-foreground-500 mt-0.5">周一至周五 9:00 - 18:00</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900">公司地址</h3>
                    <p className="text-xs text-foreground-500 mt-0.5">北京市海淀区中关村科技园区</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-foreground-900 mb-4">发送消息</h3>
                <form
                  id="contact-form"
                  data-readdy-form
                  className="space-y-3"
                  onSubmit={handleSubmit}
                >
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      required
                      className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      required
                      className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      placeholder="请输入您的消息（最多500字）"
                      maxLength={500}
                      rows={4}
                      required
                      className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="absolute opacity-0 pointer-events-none"
                    style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
                  />
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-2.5 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {formStatus === 'loading' ? '发送中...' : '发送消息'}
                  </button>
                  {formMessage && (
                    <p className={`text-xs mt-2 ${formStatus === 'success' ? 'text-secondary-600' : 'text-red-500'}`}>
                      {formMessage}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}